import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Avatar, Tag, Button, Spin, Alert, Row, Col, Typography, Image, List } from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined, EditOutlined,
  ArrowLeftOutlined, FilePdfOutlined, FileOutlined
} from '@ant-design/icons';
import { getSubcontractorEmployees, imagepath } from '../api';

const { Text } = Typography;

const labelStyle = { color: '#888', fontWeight: 500, marginBottom: 2, display: 'block' };
const valueStyle = { fontSize: 16, marginBottom: 12, display: 'block' };
const cardStyle = { background: '#fafcff', borderRadius: 14, boxShadow: '0 6px 32px rgba(0,0,0,0.14)', border: '1px solid #e0e0e0', marginBottom: 32 };

function isImage(fileName) {
  return /\.(jpg|jpeg|png)$/i.test(fileName);
}
function isPdf(fileName) {
  return /\.(pdf)$/i.test(fileName);
}

function getColor(name) {
  const colors = ['#1890ff', '#f5222d', '#faad14', '#52c41a', '#722ed1', '#13c2c2', '#eb2f96', '#b37feb'];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const SubcontractorEmployeeView = () => {
  const { subcontractorId, employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getSubcontractorEmployees(subcontractorId).then(res => {
      if (res.success) {
        const employeeData = res.data.find(e => e.id === parseInt(employeeId, 10));
        if (employeeData) {
          setEmployee(employeeData);
        } else {
          setError('Employee not found');
        }
      } else {
        setError(res.message || 'Failed to load employee');
      }
      setLoading(false);
    }).catch(err => {
      setError('Failed to load employee');
      setLoading(false);
    });
  }, [subcontractorId, employeeId]);

  if (loading) return <div style={{ textAlign: 'center', marginTop: 100 }}><Spin size="large" /></div>;
  if (error) return <Alert type="error" message={error} style={{ margin: 40 }} />;
  if (!employee) return null;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const initials = getInitials(employee.name);

  return (
    <div style={{ padding: '0px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, padding: '0 24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/subcontractors`)}
        >
          Back
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          style={{ background: '#1890ff' }}
          onClick={() => navigate(`/subcontractors/${subcontractorId}/employees/${employeeId}/edit`)}
        >
          Edit Employee
        </Button>
      </div>

      <div style={{ padding: '0 24px' }}>
        <Row gutter={32} style={{ marginBottom: 32 }} align="stretch">
          {/* Main Info Card */}
          <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Card
              size="small"
              style={{
                ...cardStyle,
                display: 'flex',
                alignItems: 'center',
                padding: 16,
                background: employee.status === 'active' ? '#f6ffed' : '#fff1f0',
                height: '100%',
                marginBottom: 0
              }}
              bordered={false}
            >
              <Avatar
                src={employee.profile_image ? `${imagepath}${employee.profile_image}` : undefined}
                size={80}
                style={{
                  backgroundColor: employee.profile_image ? undefined : getColor(employee.name),
                  color: '#fff',
                  fontSize: 32,
                  marginRight: 20,
                  objectFit: 'cover',
                  flexShrink: 0
                }}
                icon={!employee.profile_image ? <UserOutlined /> : undefined}
              >
                {!employee.profile_image && initials}
              </Avatar>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 24 }}>{employee.name}</div>
                <div style={{ color: '#888', fontSize: 16 }}>{employee.emp_code}</div>
                <div style={{ color: '#888', fontSize: 16 }}>{employee.position}</div>
                <Tag color={employee.status === 'active' ? 'green' : 'red'} style={{ marginTop: 8 }}>
                  {employee.status?.toUpperCase()}
                </Tag>
              </div>
              <div style={{ minWidth: 200, paddingLeft: 20, borderLeft: '1px solid #e0e0e0' }}>
                <div style={{ color: '#888', fontSize: 14, marginBottom: 4 }}>
                  <PhoneOutlined /> <b>UAE:</b> {employee.phone || 'N/A'}
                </div>
                <div style={{ color: '#888', fontSize: 14, marginBottom: 4 }}>
                  <PhoneOutlined /> <b>Native:</b> {employee.native_phone || 'N/A'}
                </div>
                <div style={{ color: '#888', fontSize: 14 }}>
                  <MailOutlined /> <b>Email:</b> {employee.email || 'N/A'}
                </div>
              </div>
            </Card>
          </Col>

          {/* Personal Info Card */}
          <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Card title="Personal Info" style={{ ...cardStyle, height: '100%', marginBottom: 0 }} bordered={false}>
              <Text style={labelStyle}>Date of Birth</Text>
              <Text style={valueStyle}>{employee.dob || 'N/A'}</Text>
              <Text style={labelStyle}>Address</Text>
              <Text style={valueStyle}>{employee.address || 'N/A'}</Text>
              <Text style={labelStyle}>Skill / Position</Text>
              <Text style={valueStyle}>{employee.skill?.name || employee.position || 'N/A'}</Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={32} style={{ marginBottom: 32 }} align="stretch">
          {/* Job Info Card */}
          <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Card title="Job Info" style={{ ...cardStyle, height: '100%', marginBottom: 0 }} bordered={false}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text style={labelStyle}>Joining Date</Text>
                  <Text style={valueStyle}>{employee.joining_date || 'N/A'}</Text>
                  <Text style={labelStyle}>Salary Type</Text>
                  <Text style={valueStyle}>{employee.salary_type || 'N/A'}</Text>
                  <Text style={labelStyle}>Base Salary</Text>
                  <Text style={valueStyle}>{employee.base_salary || '0'}</Text>
                </Col>
                <Col span={12}>
                  <Text style={labelStyle}>Hourly Rate</Text>
                  <Text style={valueStyle}>{employee.hourly_rate || '0'}</Text>
                  <Text style={labelStyle}>Overtime Rate</Text>
                  <Text style={valueStyle}>{employee.over_time_rate || '0'}</Text>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Documents Card */}
          <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column' }}>
            <Card title="Documents" style={{ ...cardStyle, height: '100%', marginBottom: 0 }} bordered={false}>
              <List
                grid={{ gutter: 16, xs: 1, sm: 2 }}
                dataSource={employee.documents || []}
                locale={{ emptyText: 'No documents uploaded' }}
                renderItem={doc => (
                  <List.Item>
                    <Card size="small" style={{ textAlign: 'center', background: '#f5f5f5', borderRadius: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {doc.document_type.replace(/_/g, ' ').toUpperCase()}
                      </div>
                      <div style={{ color: '#888', fontSize: 11, marginBottom: 8 }}>
                        Expiry: {doc.expiry_date || 'N/A'}
                      </div>

                      {doc.document_file && isImage(doc.document_file) ? (
                        <Image
                          src={`${imagepath}${doc.document_file}`}
                          width={40}
                          height={40}
                          style={{ objectFit: 'cover', borderRadius: 4, marginBottom: 8 }}
                          preview={true}
                        />
                      ) : doc.document_file && isPdf(doc.document_file) ? (
                        <FilePdfOutlined style={{ fontSize: 32, color: '#cf1322', marginBottom: 8 }} />
                      ) : (
                        <FileOutlined style={{ fontSize: 32, color: '#888', marginBottom: 8 }} />
                      )}

                      <div style={{ fontSize: 12 }}>
                        <a href={`${imagepath}${doc.document_file}`} target="_blank" rel="noopener noreferrer">View File</a>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SubcontractorEmployeeView;
